import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import socket from 'src/socket';
import { useAppDispatch, useAppSelector } from 'src/redux';
import { UserRole } from 'src/constants';
import { LS_KEY_AI_ASSISTANT_NUDGE_SEEN } from 'src/constants/localStorageKeys';
import {
  pollAssistantMessage,
  startCheckoutRescue,
} from 'src/redux/actions/assistant';
import {
  applyAssistantEvent,
  closeAssistant,
  closeChatCheckout,
  openAssistant,
} from 'src/redux/slices/assistant';
import type { AssistantEvent } from 'src/redux/types/assistant';
import AiCore from './AiCore';
import {
  CHAT_CHECKOUT_REASON,
  POLL_GIVE_UP_MS,
  POLL_INTERVAL_MS,
  SOCKET_GRACE_MS,
  hasBottomBar,
  isAssistantHiddenPath,
} from './constants';
import { Nudge, OrbButton, OrbDock, OrbLabel } from './index.styles';

const loadPanel = () => import('./AssistantPanel');
const AssistantPanel = React.lazy(loadPanel);
// Only for a checkout a chat card opens: kept out of the root bundle.
const BuyCreditsDialog = React.lazy(
  () => import('src/components/buyCreditsDialog'),
);

const CLOSE_ANIMATION_MS = 280;
// Lets the checkout finish closing before the chat slides in over the page.
const RESCUE_OPEN_DELAY_MS = 600;
// A sheet still up when a checkout is left -- the pitch an embedded checkout
// goes back to, a course paywall -- would hide the chat under it: wait for it
// to close, then open. Long enough to read a pitch; past that, let it go.
const RESCUE_WAIT_POLL_MS = 1000;
const RESCUE_WAIT_MAX_MS = 10 * 60 * 1000;
const isModalOpen = () =>
  !!document.querySelector('.MuiModal-root:not(.MuiModal-hidden)');
const NUDGE_DELAY_MS = 2500;
const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.platform);

const readFlag = (key: string) => {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return true;
  }
};

/**
 * Keeps an in-flight reply moving when its socket pushes are not arriving
 * (socket down, a missed room join): after a quiet spell, read it over HTTP
 * until it finishes.
 */
const useReplyFallback = () => {
  const dispatch = useAppDispatch();
  const live = useAppSelector((state) =>
    state.assistant.messages.find(
      (m) =>
        m.role === 'assistant' &&
        (m.status === 'pending' || m.status === 'streaming'),
    ),
  );
  const liveId = live?.id;
  const liveSeq = live?.seq ?? 0;
  const startedAt = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!liveId) return undefined;
    startedAt.current[liveId] ??= Date.now();
    let poller: number | undefined;
    const quiet = window.setTimeout(() => {
      poller = window.setInterval(() => {
        if (Date.now() - startedAt.current[liveId] > POLL_GIVE_UP_MS) {
          window.clearInterval(poller);
          return;
        }
        dispatch(pollAssistantMessage(liveId));
      }, POLL_INTERVAL_MS);
    }, SOCKET_GRACE_MS);
    return () => {
      window.clearTimeout(quiet);
      window.clearInterval(poller);
    };
  }, [liveId, liveSeq, dispatch]);
};

interface AssistantOrbProps {
  onOpen: () => void;
  hidden: boolean;
  lifted: boolean;
  name: string;
}

const AssistantOrb = ({ onOpen, hidden, lifted, name }: AssistantOrbProps) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));
  const [hover, setHover] = useState(false);
  const [nudge, setNudge] = useState(false);

  // Phones never hover, so fetch the panel once the page has settled.
  useEffect(() => {
    const idle = (window as any).requestIdleCallback ?? window.setTimeout;
    idle(() => loadPanel(), { timeout: 4000 });
  }, []);

  useEffect(() => {
    if (readFlag(LS_KEY_AI_ASSISTANT_NUDGE_SEEN)) return undefined;
    const id = window.setTimeout(() => setNudge(true), NUDGE_DELAY_MS);
    return () => window.clearTimeout(id);
  }, []);

  const dismissNudge = () => {
    setNudge(false);
    try {
      localStorage.setItem(LS_KEY_AI_ASSISTANT_NUDGE_SEEN, '1');
    } catch {
      // Private mode: it shows again next visit, which is harmless.
    }
  };

  const warm = () => {
    setHover(true);
    loadPanel();
  };

  return (
    <OrbDock lifted={lifted} hidden={hidden}>
      {nudge && !hidden ? (
        <Nudge role="status">
          Hi {name}! I&apos;m <b>Abekus AI</b>. Ask me anything about Abekus, or
          tell me what to do.
          <IconButton
            size="small"
            onClick={dismissNudge}
            aria-label="Dismiss"
            sx={{ position: 'absolute', top: 4, right: 4 }}
          >
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Nudge>
      ) : (
        <OrbLabel show={hover}>
          Ask Abekus AI
          <kbd>{isMac ? '⌘' : 'Ctrl'} K</kbd>
        </OrbLabel>
      )}
      <OrbButton
        onClick={() => {
          dismissNudge();
          onOpen();
        }}
        onMouseEnter={warm}
        onMouseLeave={() => setHover(false)}
        onFocus={warm}
        onBlur={() => setHover(false)}
        aria-label="Open Abekus AI assistant"
      >
        <AiCore size={isPhone ? 54 : 60} mood={hover ? 'hover' : 'idle'} />
      </OrbButton>
    </OrbDock>
  );
};

/**
 * A Pro checkout left unfinished (checkoutAbandoned): back to the chat when a
 * chat card opened it, else ask the server for a follow-up chat, which opens
 * the panel when one is due (startCheckoutRescue).
 */
const useCheckoutRescue = (active: boolean) => {
  const dispatch = useAppDispatch();
  const { abandonedCheckout, isOpen } = useAppSelector(
    (state) => state.assistant,
  );
  const latest = useRef({ active, isOpen });
  useEffect(() => {
    latest.current = { active, isOpen };
  }, [active, isOpen]);

  useEffect(() => {
    if (!abandonedCheckout) return undefined;
    let id: number;
    const attempt = () => {
      const now = latest.current;
      if (!now.active || now.isOpen) return;
      if (isModalOpen()) {
        if (Date.now() - abandonedCheckout.at < RESCUE_WAIT_MAX_MS) {
          id = window.setTimeout(attempt, RESCUE_WAIT_POLL_MS);
        }
        return;
      }
      if (abandonedCheckout.reason === CHAT_CHECKOUT_REASON) {
        dispatch(openAssistant());
      } else {
        dispatch(
          startCheckoutRescue({
            reason: abandonedCheckout.reason,
            path: abandonedCheckout.path,
          }),
        );
      }
    };
    id = window.setTimeout(attempt, RESCUE_OPEN_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [abandonedCheckout, dispatch]);
};

/**
 * The applicant-side AI assistant: a floating orb on every applicant page
 * except focus screens, opening a chat that knows the current page and can
 * act across the platform. Mounted once at the router root so a reply keeps
 * streaming -- and the conversation stays put -- as the user moves between pages.
 */
const AiAssistant = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { user, isLoginSuccess } = useAppSelector((state) => state.auth);
  const isOpen = useAppSelector((state) => state.assistant.isOpen);
  const chatCheckoutItemId = useAppSelector(
    (state) => state.assistant.chatCheckoutItemId,
  );
  const [closing, setClosing] = useState(false);

  // The native app has its own navigation shell around this web view.
  const isInWebView = !!(window as any)?.ReactNativeWebView;
  const eligible =
    isLoginSuccess &&
    !!user?.id &&
    user?.user_type === UserRole.Applicant &&
    !isInWebView;
  const hiddenHere = isAssistantHiddenPath(pathname);

  useReplyFallback();
  useCheckoutRescue(eligible && !hiddenHere);

  useEffect(() => {
    if (!eligible) return undefined;
    const onEvent = (event: AssistantEvent) =>
      dispatch(applyAssistantEvent(event));
    socket.on('assistant_event', onEvent);
    return () => {
      socket.off('assistant_event', onEvent);
    };
  }, [eligible, dispatch]);

  const open = useCallback(() => dispatch(openAssistant()), [dispatch]);

  const close = useCallback(() => {
    setClosing(true);
    window.setTimeout(() => {
      dispatch(closeAssistant());
      setClosing(false);
    }, CLOSE_ANIMATION_MS);
  }, [dispatch]);

  useEffect(() => {
    // Not over the checkout a chat card opened: it would trap the panel's focus.
    if (!eligible || hiddenHere || chatCheckoutItemId) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (isOpen) close();
        else open();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [eligible, hiddenHere, chatCheckoutItemId, isOpen, open, close]);

  // Walking into a focus screen puts the panel away rather than over the test.
  useEffect(() => {
    if (hiddenHere && isOpen) dispatch(closeAssistant());
  }, [hiddenHere, isOpen, dispatch]);

  if (!eligible || hiddenHere) return null;

  return (
    <Box component="aside">
      <AssistantOrb
        onOpen={open}
        hidden={isOpen}
        lifted={hasBottomBar(pathname)}
        name={(user?.first_name ?? '').trim() || 'there'}
      />
      {isOpen && (
        <Suspense fallback={null}>
          <AssistantPanel closing={closing} onClose={close} />
        </Suspense>
      )}
      {chatCheckoutItemId && (
        <Suspense fallback={null}>
          <BuyCreditsDialog
            open
            directPurchase={{ itemId: chatCheckoutItemId }}
            trigger="browse"
            reason={CHAT_CHECKOUT_REASON}
            onClose={() => dispatch(closeChatCheckout())}
          />
        </Suspense>
      )}
    </Box>
  );
};

export default AiAssistant;
